import { FileTextIcon, PlusIcon, SendIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router'
import { Boton } from '@/components/ui/Boton'
import { cn } from '@/lib/cn'
import { MenuItem } from '@/components/ui/MenuDesplegable'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { useSesionActiva } from '@/features/auth/RutaProtegida'
import { guardarUltimoProyecto } from '@/features/auth/useSesion'
import { Confirmacion } from '@/features/admin/Confirmacion'
import { ModalEquipo } from '@/features/admin/ModalEquipo'
import { EncabezadoProyecto } from '@/features/proyecto/EncabezadoProyecto'
import {
  useDarDeBaja,
  useEnviarBorradores,
  useGrilla,
  usePublicacion,
  useReprogramar,
  useRestaurar,
  type FiltrosGrilla,
} from '@/features/publicacion/api'
import { ModalDatos } from '@/features/publicacion/ModalDatos'
import { ModalPublicacion } from '@/features/publicacion/ModalPublicacion'
import { ModalNuevaVersion } from '@/features/publicacion/ModalNuevaVersion'
import { ModalReprogramar } from '@/features/publicacion/ModalReprogramar'
import { PanelPublicacion } from '@/features/publicacion/PanelPublicacion'
import {
  FILTROS_DESTINO,
  type FiltroDestino,
  type PublicacionResumen,
} from '@/lib/schemas/publicaciones'
import type { PanelPublicacion as PanelDatos } from '@/lib/schemas/revision'
import { Pagina } from '@/components/Pagina'
import { VistaCalendario } from '@/features/calendario/VistaCalendario'
import { VistaPlanificacion } from '@/features/planificacion/VistaPlanificacion'
import { VistaRecursos } from '@/features/recursos/VistaRecursos'
import { useCarpetaDrive } from '@/lib/drive/useCarpetaDrive'
import { useEspacioDisponible } from '@/lib/useEspacioDisponible'
import { BarraVistas, Herramientas, type Vista } from './Herramientas'
import { VerificacionCuenta } from '@/features/auth/VerificacionCuenta'
import { medidasGrilla, SEPARACION_GRILLA } from './medidas'
import { useColumnas } from './useColumnas'
import { TarjetaPublicacion } from './TarjetaPublicacion'
import { formatearDiaYMes, formatearMes, mesActual, rangoDelMes } from './formato'

const POR_TANDA = 24

/**
 * P01 · grilla del proyecto (§8.2). Tres columnas de portadas 4:5 ordenadas
 * por fecha, con el espacio reservado para que no salte al cargar. Los
 * filtros de mes y pilar se guardan en la URL, así se conservan al ir y
 * volver. El panel de publicación (C3) llega en la Fase 4: por ahora, abrir
 * una tarjeta marca la visita y, para RASTRO, ofrece editarla.
 */
export function Grilla() {
  const { proyectoId = '' } = useParams()
  const { usuario, proyectos } = useSesionActiva()
  const proyecto = proyectos.find((p) => p.id === proyectoId)
  const esRastro = usuario.tipo === 'rastro'
  const avisar = useAvisar()

  const [params, setParams] = useSearchParams()
  const [limite, setLimite] = useState(POR_TANDA)
  const [nuevaAbierta, setNuevaAbierta] = useState(false)
  /** Fecha precargada al crear desde el "+" de un día del calendario. */
  const [fechaNueva, setFechaNueva] = useState<string | null>(null)
  const [editando, setEditando] = useState<PublicacionResumen | null>(null)
  const [reprogramando, setReprogramando] = useState<PublicacionResumen | null>(null)
  const [dandoDeBaja, setDandoDeBaja] = useState<PublicacionResumen | null>(null)
  const [enviandoTodos, setEnviandoTodos] = useState(false)
  const [abierta, setAbierta] = useState<string | null>(null)
  const [secuencial, setSecuencial] = useState(false)
  const [nuevaVersionDe, setNuevaVersionDe] = useState<PanelDatos | null>(null)
  const [equipoAbierto, setEquipoAbierto] = useState(false)
  const [refGrilla, espacioGrilla] = useEspacioDisponible<HTMLDivElement>()
  const [columnas, setColumnas] = useColumnas()
  const medidas = espacioGrilla ? medidasGrilla(espacioGrilla, columnas) : null

  // Grilla es la vista de entrada; Planificación y Recursos llegan con las Fases 5a y 5b.
  const pedida = params.get('vista')
  const vista: Vista =
    pedida === 'calendario' || pedida === 'planificacion' || pedida === 'recursos'
      ? pedida
      : 'grilla'
  const deContenido = vista === 'grilla' || vista === 'calendario'
  // Grilla y calendario se paran en un mes; sin mes en la URL, el corriente.
  // La grilla además se puede ver completa (?mes=todos, "Ver grilla
  // completa"); el calendario, no: un calendario sin mes no existe.
  const mesElegido = params.get('mes')
  const completa = vista === 'grilla' && mesElegido === 'todos'
  const mes = mesElegido && mesElegido !== 'todos' ? mesElegido : mesActual()
  // Los filtros son de RASTRO: el cliente revisa siempre el mes completo, y
  // un enlace con ?pilar= no le deja una vista parcial sin que lo sepa.
  const pilarIds = esRastro ? (params.get('pilar') ?? '').split(',').filter(Boolean) : []
  // Feed o historias (DEC-21) es para todos, y a la vista: las dos vistas abren
  // con todo, así el cliente siempre ve lo que tiene para revisar (OBS-017).
  const destino: FiltroDestino =
    FILTROS_DESTINO.find((d) => d !== 'todo' && d === params.get('destino')) ?? 'todo'

  const filtros: FiltrosGrilla =
    vista === 'calendario'
      ? {
          // La cuadrícula trae los días de los meses vecinos, así que se pide
          // por rango y no por mes, y de una sola vez: no hay "Mostrar más".
          mes: null,
          rango: rangoDelMes(mes),
          pilarIds,
          destino,
          mostrarDeBaja: false,
          limite: 1000,
        }
      : {
          mes: completa ? null : mes,
          pilarIds,
          destino,
          mostrarDeBaja: esRastro && params.get('baja') === '1',
          limite,
        }

  // Planificación y Recursos no usan la grilla: no se la pide.
  const grilla = useGrilla(deContenido ? proyectoId : '', filtros)
  // La carpeta del cliente, para el selector de Drive de Planificación y Recursos.
  const datosCarpeta = useCarpetaDrive(proyectoId, esRastro && !deContenido)
  const detalle = usePublicacion(editando?.id ?? null)
  const darDeBaja = useDarDeBaja()
  const restaurar = useRestaurar()
  const enviarBorradores = useEnviarBorradores()
  const reprogramar = useReprogramar()

  useEffect(() => {
    if (proyecto) guardarUltimoProyecto(proyecto.id)
  }, [proyecto])

  // DEC-18: el botón "Ver y aprobar" del mail llega con ?revisar=1 y abre la
  // revisión, como "Empezar revisión". Se atiende una vez, en el render (el
  // patrón de React para ajustar estado), y después se saca de la URL.
  const pideRevisar = params.get('revisar') === '1'
  const [revisarAtendido, setRevisarAtendido] = useState(false)
  if (pideRevisar && grilla.data !== undefined && !revisarAtendido) {
    setRevisarAtendido(true)
    const primera = grilla.data.primeraParaRevisar
    if (primera && !esRastro) {
      setSecuencial(true)
      setAbierta(primera)
    }
  }
  useEffect(() => {
    if (!revisarAtendido || !pideRevisar) return
    const siguiente = new URLSearchParams(params)
    siguiente.delete('revisar')
    setParams(siguiente, { replace: true })
  }, [revisarAtendido, pideRevisar, params, setParams])

  // Si no se eligió mes y el corriente está vacío, se salta al mes con
  // publicaciones más cercano: el próximo, o si no hay, el último.
  const meses = grilla.data?.meses
  const vacio = grilla.data?.publicaciones.length === 0
  useEffect(() => {
    if (
      (mesElegido && mesElegido !== 'todos') ||
      completa ||
      // Con un filtro puesto, el mes vacío es del filtro: no se salta de mes.
      destino !== 'todo' ||
      !vacio ||
      !meses ||
      meses.length === 0 ||
      meses.includes(mes)
    )
      return
    const otroMes = [...meses].reverse().find((m) => m > mes) ?? meses[0]
    if (!otroMes) return
    setParams(
      (p) => {
        const siguiente = new URLSearchParams(p)
        siguiente.set('mes', otroMes)
        return siguiente
      },
      { replace: true },
    )
  }, [mesElegido, completa, destino, vacio, meses, mes, setParams])

  const limpiarFiltros = () => {
    const siguiente = new URLSearchParams(params)
    siguiente.delete('pilar')
    siguiente.delete('baja')
    siguiente.delete('destino')
    setParams(siguiente, { replace: true })
    setLimite(POR_TANDA)
  }

  const cambiarVista = (v: Vista) => {
    const siguiente = new URLSearchParams(params)
    if (v === 'grilla') siguiente.delete('vista')
    else siguiente.set('vista', v)
    if (v !== 'grilla') siguiente.delete('baja')
    if (v !== 'grilla' && siguiente.get('mes') === 'todos') siguiente.delete('mes')
    setParams(siguiente, { replace: true })
    setLimite(POR_TANDA)
  }

  const cambiarFiltro = (clave: string, valor: string | null) => {
    const siguiente = new URLSearchParams(params)
    if (valor) siguiente.set(clave, valor)
    else siguiente.delete(clave)
    setParams(siguiente, { replace: true })
    setLimite(POR_TANDA)
  }

  if (!proyecto) return <Navigate to="/" replace />

  const datos = grilla.data
  const publicaciones = datos?.publicaciones ?? []
  const hayFiltros =
    filtros.pilarIds.length > 0 || filtros.mostrarDeBaja || destino !== 'todo'

  // En celular "Enviar borradores" no entra en la fila: va al menú ⋮.
  const enviarTodosMenu =
    esRastro && (datos?.borradores ?? 0) > 0 ? (
      <MenuItem
        onSelect={() => {
          setEnviandoTodos(true)
        }}
      >
        <SendIcon className="size-4" aria-hidden />
        Enviar {datos?.borradores} {datos?.borradores === 1 ? 'borrador' : 'borradores'} a
        revisión
      </MenuItem>
    ) : undefined

  const accionesRastro = esRastro ? (
    <>
      {(datos?.borradores ?? 0) > 0 && (
        <Boton
          variante="secundario"
          tamano="chico"
          className="hidden sm:inline-flex"
          onClick={() => {
            setEnviandoTodos(true)
          }}
        >
          <SendIcon className="size-4" aria-hidden />
          Enviar {datos?.borradores} {datos?.borradores === 1 ? 'borrador' : 'borradores'}{' '}
          a revisión
        </Boton>
      )}
      <Boton
        tamano="chico"
        className="hidden sm:inline-flex"
        onClick={() => {
          setNuevaAbierta(true)
        }}
      >
        <PlusIcon className="size-4" aria-hidden />
        Nueva publicación
      </Boton>
    </>
  ) : undefined

  return (
    <>
      <VerificacionCuenta />
      <EncabezadoProyecto
        proyecto={proyecto}
        // Las acciones de RASTRO van en la barra de arriba: en la fila de
        // herramientas la partían en dos renglones y en un monitor de 1360×768
        // le quitaban a la grilla una fila de tarjetas.
        {...(deContenido && accionesRastro ? { acciones: accionesRastro } : {})}
        alAbrirEquipo={() => {
          setEquipoAbierto(true)
        }}
      />

      <Herramientas
        vista={vista}
        // Sólo en escritorio, donde la grilla se arma según la pantalla.
        {...(espacioGrilla
          ? { columnas: { valor: columnas, alCambiar: setColumnas } }
          : {})}
        alCambiarVista={cambiarVista}
        mes={mes}
        alCambiarMes={(m) => {
          cambiarFiltro('mes', m)
        }}
        completa={completa}
        filtroDestino={{
          valor: destino,
          alCambiar: (d) => {
            cambiarFiltro('destino', d === 'todo' ? null : d)
          },
        }}
        alCambiarCompleta={(v) => {
          cambiarFiltro('mes', v ? 'todos' : null)
        }}
        {...(esRastro
          ? {
              filtrosRastro: {
                pilares: datos?.pilares ?? [],
                pilarIds,
                alCambiarPilares: (ids: string[]) => {
                  cambiarFiltro('pilar', ids.join(',') || null)
                },
                mostrarDeBaja: filtros.mostrarDeBaja,
                alCambiarDeBaja: (v: boolean) => {
                  cambiarFiltro('baja', v ? '1' : null)
                },
                alQuitarTodos: limpiarFiltros,
              },
            }
          : {})}
        accionesMenu={enviarTodosMenu}
      />

      <Pagina as="main" className="py-5 pb-40 sm:pb-24">
        {/* §9 — aviso de revisión del cliente. */}
        {deContenido && !esRastro && (datos?.paraRevisar ?? 0) > 0 && (
          <section
            role="status"
            className="mb-5 flex flex-wrap items-center gap-4 rounded-tarjeta bg-tarjeta px-5 py-4 shadow-tarjeta"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-revisar text-revisar-tinta">
              <FileTextIcon className="size-5" aria-hidden />
            </span>
            <p className="min-w-0 flex-1 text-sm">
              Tenés {datos?.paraRevisar}{' '}
              {datos?.paraRevisar === 1 ? 'publicación' : 'publicaciones'} para revisar.
              Te lleva unos minutos.
            </p>
            <Boton
              onClick={() => {
                // De todo el proyecto, no sólo del mes que se está mirando.
                const primera = datos?.primeraParaRevisar
                if (primera) {
                  setSecuencial(true)
                  setAbierta(primera)
                }
              }}
            >
              Empezar revisión
            </Boton>
          </section>
        )}

        {vista === 'planificacion' ? (
          <VistaPlanificacion
            proyectoId={proyectoId}
            esRastro={esRastro}
            carpetaDrive={datosCarpeta.data ?? null}
          />
        ) : vista === 'recursos' ? (
          <VistaRecursos
            proyectoId={proyectoId}
            esRastro={esRastro}
            carpetaDrive={datosCarpeta.data ?? null}
          />
        ) : vista === 'calendario' ? (
          <VistaCalendario
            mes={mes}
            publicaciones={publicaciones}
            esRastro={esRastro}
            filtrando={destino !== 'todo'}
            cargando={grilla.isPending}
            alAbrir={(id) => {
              setSecuencial(false)
              setAbierta(id)
            }}
            alCrearEn={(fecha) => {
              setFechaNueva(fecha)
              setNuevaAbierta(true)
            }}
            alMover={(p, fecha) => {
              const original = p.fechaPautada
              // La pieza ya se ve en el día nuevo (useReprogramar es optimista).
              reprogramar
                .mutateAsync({ id: p.id, fechaPautada: fecha })
                .then(() => {
                  avisar(`Reprogramada al ${formatearDiaYMes(fecha)}`, {
                    accion: {
                      etiqueta: 'Deshacer',
                      alHacer: () => {
                        void reprogramar.mutateAsync({
                          id: p.id,
                          fechaPautada: original,
                        })
                      },
                    },
                  })
                })
                .catch(() => {
                  avisar('No se pudo reprogramar. La pieza volvió a su día.')
                })
            }}
          />
        ) : grilla.isPending ? (
          <p className="py-10 text-center text-sm text-tinta-suave">Cargando…</p>
        ) : publicaciones.length === 0 ? (
          <Vacio
            esRastro={esRastro}
            hayFiltros={hayFiltros}
            mes={formatearMes(mes).toLowerCase()}
            proyectoVacio={(datos?.meses.length ?? 0) === 0}
            alLimpiar={limpiarFiltros}
            alCrear={() => {
              setNuevaAbierta(true)
            }}
          />
        ) : (
          <>
            {/*
              Celular y tableta: tres columnas, como el perfil de Instagram, con
              las piezas casi pegadas en el celular (pedido de Serena, 22/09).
              Escritorio: la persona elige 3, 4, 5 o 6 columnas (DEC-08, OBS-013)
              y las tarjetas llenan el ancho; si achicándolas un poco entran
              filas enteras, se achican (medidasGrilla). Al abrir la pieza se
              ve grande, para leer el texto que trae adentro.
            */}
            <div
              ref={refGrilla}
              data-columnas={medidas?.columnas ?? 3}
              className={cn('grid gap-1 sm:gap-4', !medidas && 'grid-cols-3')}
              style={
                medidas
                  ? {
                      gridTemplateColumns: `repeat(${String(medidas.columnas)}, ${String(medidas.ancho)}px)`,
                      gap: SEPARACION_GRILLA,
                      justifyContent: 'center',
                    }
                  : undefined
              }
            >
              {publicaciones.map((p) => (
                <TarjetaPublicacion
                  key={p.id}
                  publicacion={p}
                  esRastro={esRastro}
                  angosta={(medidas?.ancho ?? Infinity) < 220}
                  alAbrir={(id) => {
                    setSecuencial(false)
                    setAbierta(id)
                  }}
                />
              ))}
            </div>

            {(datos?.hayMas ?? 0) > 0 && (
              <div className="mt-6 flex justify-center">
                <Boton
                  variante="secundario"
                  onClick={() => {
                    setLimite((n) => n + POR_TANDA)
                  }}
                >
                  Mostrar más
                </Boton>
              </div>
            )}
          </>
        )}
      </Pagina>

      <BarraVistas vista={vista} alCambiarVista={cambiarVista} />

      {/* Botón flotante de celular (§5.2), encima de la barra de pestañas. */}
      {esRastro && deContenido && (
        <div className="fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-30 sm:hidden">
          <Boton
            tamano="icono"
            className="size-14 shadow-tarjeta"
            aria-label="Nueva publicación"
            onClick={() => {
              setNuevaAbierta(true)
            }}
          >
            <PlusIcon className="size-6" aria-hidden />
          </Boton>
        </div>
      )}

      <ModalPublicacion
        abierto={nuevaAbierta}
        fechaInicial={fechaNueva}
        alCerrar={() => {
          setNuevaAbierta(false)
          setFechaNueva(null)
        }}
        proyectoId={proyectoId}
        usuarioInstagram={datos?.proyecto.usuarioInstagram ?? ''}
        pilares={datos?.pilares ?? []}
        carpetaDrive={datos?.proyecto.driveFolderId ?? null}
        publicacion={null}
      />

      {editando && detalle.data?.estado === 'borrador' && (
        <ModalPublicacion
          abierto
          alCerrar={() => {
            setEditando(null)
          }}
          proyectoId={proyectoId}
          usuarioInstagram={datos?.proyecto.usuarioInstagram ?? ''}
          pilares={datos?.pilares ?? []}
          carpetaDrive={datos?.proyecto.driveFolderId ?? null}
          publicacion={detalle.data}
        />
      )}

      {editando && detalle.data && detalle.data.estado !== 'borrador' ? (
        <ModalDatos
          abierto
          alCerrar={() => {
            setEditando(null)
          }}
          publicacion={detalle.data}
          pilares={datos?.pilares ?? []}
        />
      ) : null}

      {reprogramando && (
        <ModalReprogramar
          abierto
          alCerrar={() => {
            setReprogramando(null)
          }}
          publicacion={reprogramando}
        />
      )}

      <Confirmacion
        abierto={dandoDeBaja !== null}
        alCerrar={() => {
          setDandoDeBaja(null)
        }}
        titulo="Dar de baja"
        texto="La publicación deja de verse para el cliente. Se recupera con “Mostrar dadas de baja”, desde su panel. El archivo en Drive no se toca."
        confirmar="Dar de baja"
        peligro
        alConfirmar={async () => {
          if (!dandoDeBaja) return
          await darDeBaja.mutateAsync(dandoDeBaja.id)
          avisar('Publicación dada de baja')
        }}
      />

      <Confirmacion
        abierto={enviandoTodos}
        alCerrar={() => {
          setEnviandoTodos(false)
        }}
        titulo="Enviar borradores a revisión"
        texto={`Vas a enviar ${String(datos?.borradores ?? 0)} ${
          (datos?.borradores ?? 0) === 1 ? 'publicación' : 'publicaciones'
        } a revisión. El cliente las va a ver enseguida.`}
        confirmar="Enviar"
        alConfirmar={async () => {
          const r = await enviarBorradores.mutateAsync(proyectoId)
          avisar(
            `${String(r.enviadas)} ${r.enviadas === 1 ? 'publicación enviada' : 'publicaciones enviadas'} a revisión`,
          )
        }}
      />

      {abierta && (
        <PanelPublicacion
          publicacionId={abierta}
          esRastro={esRastro}
          secuencial={secuencial}
          alCerrar={() => {
            setAbierta(null)
            setSecuencial(false)
          }}
          alIrA={setAbierta}
          alEditar={(p) => {
            setAbierta(null)
            // Un borrador se edita entero; el resto va por una versión nueva.
            if (p.estado === 'borrador') {
              const enGrilla = publicaciones.find((x) => x.id === p.id)
              if (enGrilla) setEditando(enGrilla)
            } else {
              setNuevaVersionDe(p)
            }
          }}
          alReprogramar={(p) => {
            const enGrilla = publicaciones.find((x) => x.id === p.id)
            if (enGrilla) {
              setAbierta(null)
              setReprogramando(enGrilla)
            }
          }}
          alEditarDatos={(p) => {
            const enGrilla = publicaciones.find((x) => x.id === p.id)
            if (enGrilla) {
              setAbierta(null)
              setEditando(enGrilla)
            }
          }}
          alDarDeBaja={(p) => {
            const enGrilla = publicaciones.find((x) => x.id === p.id)
            if (enGrilla) {
              setAbierta(null)
              setDandoDeBaja(enGrilla)
            }
          }}
          alRestaurar={(p) => {
            setAbierta(null)
            void restaurar.mutateAsync(p.id).then(() => {
              avisar('Publicación restaurada')
            })
          }}
        />
      )}

      {nuevaVersionDe && (
        <ModalNuevaVersion
          abierto
          alCerrar={() => {
            setNuevaVersionDe(null)
          }}
          publicacion={nuevaVersionDe}
          carpetaDrive={datos?.proyecto.driveFolderId ?? null}
        />
      )}

      <ModalEquipo
        abierto={equipoAbierto}
        alCerrar={() => {
          setEquipoAbierto(false)
        }}
      />
    </>
  )
}

function Vacio({
  esRastro,
  hayFiltros,
  mes,
  proyectoVacio,
  alLimpiar,
  alCrear,
}: {
  esRastro: boolean
  hayFiltros: boolean
  /** "Septiembre 2026". */
  mes: string
  /** Sin publicaciones en ningún mes: el primer mes del proyecto. */
  proyectoVacio: boolean
  alLimpiar: () => void
  alCrear: () => void
}) {
  if (hayFiltros) {
    return (
      <section className="rounded-tarjeta bg-tarjeta px-6 py-12 text-center shadow-tarjeta">
        <p className="text-sm text-tinta-suave">
          Ninguna publicación coincide con los filtros.
        </p>
        <Boton variante="secundario" className="mt-4" onClick={alLimpiar}>
          Limpiar filtros
        </Boton>
      </section>
    )
  }
  return (
    <section className="rounded-tarjeta bg-tarjeta px-6 py-12 text-center shadow-tarjeta">
      <h1 className="text-lg font-semibold">
        {!proyectoVacio
          ? `No hay publicaciones en ${mes}.`
          : esRastro
            ? 'Este proyecto no tiene publicaciones.'
            : 'Todavía no hay publicaciones.'}
      </h1>
      <p className="mt-1 text-sm text-tinta-suave">
        {!proyectoVacio
          ? esRastro
            ? 'Cargá una, o pasá a otro mes con las flechas.'
            : 'Pasá a otro mes con las flechas.'
          : esRastro
            ? 'Cargá la primera.'
            : 'RASTRO está preparando tu primer mes.'}
      </p>
      {esRastro && (
        <Boton className="mt-4" onClick={alCrear}>
          <PlusIcon className="size-4" aria-hidden />
          Nueva publicación
        </Boton>
      )}
    </section>
  )
}
